import { useEffect, useRef } from 'react';
import { create } from 'zustand';
import { useModalHistory } from 'context/ModalRouter';

type ChecklistVisibilityState = {
  isCollapsed: boolean;
  hasAutoExpanded: boolean;
  setCollapsed: (value: boolean) => void;
  autoExpand: () => void;
};

export const useChecklistVisibility = create<ChecklistVisibilityState>()(
  (set) => ({
    isCollapsed: true,
    hasAutoExpanded: false,
    setCollapsed: (value: boolean) => set({ isCollapsed: value }),
    autoExpand: () => set({ isCollapsed: false, hasAutoExpanded: true }),
  }),
);

export const useChecklistAutoExpand = () => {
  const modalHistory = useModalHistory();
  const autoExpand = useChecklistVisibility((state) => state.autoExpand);
  // Closed modals stay in activeModals with open: false until their route unmounts,
  // so the open flag is what tells us whether anything is on screen right now.
  const hasOpenModal = modalHistory.activeModals.some((modal) => modal.open);
  const wasModalOpen = useRef(false);

  useEffect(() => {
    if (hasOpenModal) {
      wasModalOpen.current = true;
      return;
    }

    const hasClosedModal = wasModalOpen.current;
    wasModalOpen.current = false;

    // Expand once nothing covers the checklist: on load, and every time a modal
    // (the welcome modal included) closes.
    if (hasClosedModal || !useChecklistVisibility.getState().hasAutoExpanded) {
      autoExpand();
    }
  }, [hasOpenModal, autoExpand]);
};
